import {
  PrimaryLocationIds,
  validateHospitalSegmentLegs,
  validateReturnSegmentLegs,
  validateTxCenterSegmentLegs,
} from '@lp/features/RoutePlanner/utils';
import { ZonedDateTime } from '@internationalized/date';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { AviaLegTypes, GroundLegTypes, SegmentTypeEnum } from '@/api/apis/route';
import { CaseFacility } from '@/api/apis/case';

const NO_TXC_MESSAGE = 'No TXC is known yet. Start with the known segment first.';
const NO_ROUTE_MESSAGE = 'No route is built yet. Start with the full route option first.';
const NO_ROUTE_WITH_TXC_MESSAGE =
  'Although the TXC is known, no route is built yet. Start with the full route option first.';
const RE_ALLOCATION_MESSAGE =
  'The segment/route is not applicable for re-allocation. Choose the option that covers the missing legs or needs update.';
const ALREADY_BUILT_MESSAGE =
  'The segment/route is already built. Choose the option that covers the missing legs or needs update.';
const PARTIALLY_BUILT_MESSAGE =
  'The segment/route is partially built. Choose the option that covers the missing legs or needs update.';
const DONOR_OR_IN_PAST_MESSAGE =
  'The Donor OR is in the past. Update the Donor OR to a future time or select a partial route option that starts from the current time or later.';

type SegmentValidity = {
  isHubDHSegmentValid: boolean;
  isTxSegmentValid: boolean;
  isReturnSegmentValid: boolean;
  hasFullRouteBuilt: boolean;
};

export const getSegmentValidity = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  primary: PrimaryLocationIds = {},
): SegmentValidity => {
  const isHubDHSegmentValid = validateHospitalSegmentLegs(legs, primary);
  const isTxSegmentValid = validateTxCenterSegmentLegs(legs, primary);
  const isReturnSegmentValid = validateReturnSegmentLegs(legs, SegmentTypeEnum.hub, primary);

  return {
    isHubDHSegmentValid,
    isTxSegmentValid,
    isReturnSegmentValid,
    hasFullRouteBuilt: isHubDHSegmentValid && isTxSegmentValid && isReturnSegmentValid,
  };
};

const getDisabledByRouteState = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  reAllocationCase: boolean,
  txCenterDetails: CaseFacility | null,
  primary?: PrimaryLocationIds,
): Partial<Record<RouteTypeSettingsEnum, string>> => {
  if (!txCenterDetails) {
    return {
      [RouteTypeSettingsEnum.Full]: NO_TXC_MESSAGE,
      [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: NO_TXC_MESSAGE,
      [RouteTypeSettingsEnum.Custom_TXC_Hub]: NO_TXC_MESSAGE,
    };
  }

  if (reAllocationCase) {
    return {
      [RouteTypeSettingsEnum.Hub_DH]: RE_ALLOCATION_MESSAGE,
    };
  }

  const mainSegmentLegs = legs.filter((l) =>
    [SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter, SegmentTypeEnum.hub].includes(l.segmentType),
  );

  if (!mainSegmentLegs.length) {
    return {
      [RouteTypeSettingsEnum.Hub_DH]: NO_ROUTE_WITH_TXC_MESSAGE,
      [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: NO_ROUTE_MESSAGE,
      [RouteTypeSettingsEnum.Custom_TXC_Hub]: NO_ROUTE_MESSAGE,
    };
  }

  const { isHubDHSegmentValid, hasFullRouteBuilt } = getSegmentValidity(mainSegmentLegs, primary);

  if (hasFullRouteBuilt) {
    return {
      [RouteTypeSettingsEnum.Hub_DH]: ALREADY_BUILT_MESSAGE,
    };
  }

  if (!isHubDHSegmentValid) {
    return {
      [RouteTypeSettingsEnum.Hub_DH]: PARTIALLY_BUILT_MESSAGE,
      [RouteTypeSettingsEnum.Custom_TXC_Hub]: PARTIALLY_BUILT_MESSAGE,
    };
  }

  return {
    [RouteTypeSettingsEnum.Hub_DH]: ALREADY_BUILT_MESSAGE,
    [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: ALREADY_BUILT_MESSAGE,
  };
};

export const getDisabledRouteTypes = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  reAllocationCase: boolean,
  txCenterDetails: CaseFacility | null,
  donorOrTime?: ZonedDateTime,
  primary?: PrimaryLocationIds,
): Partial<Record<RouteTypeSettingsEnum, string>> => {
  const disabledRouteTypes = getDisabledByRouteState(legs, reAllocationCase, txCenterDetails, primary);
  const isDonorOrInPast = !!donorOrTime && donorOrTime.toDate().getTime() < Date.now();
  const isAlreadyDisabled = !!disabledRouteTypes[RouteTypeSettingsEnum.Custom_DH_TXC_Hub];

  return isDonorOrInPast && !isAlreadyDisabled
    ? { ...disabledRouteTypes, [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: DONOR_OR_IN_PAST_MESSAGE }
    : disabledRouteTypes;
};
